import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { createStudent } from "../../api/studentApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const categoryApi = createLookupApi("student-categories");

// Matches digicampusbd's real fixed column headers for this template exactly
// (confirmed by reading the old app's own client-side xlsx.full.min.js parser).
const COLUMN_MAP = {
  "student id": "customStudentId",
  name: "studentName",
  "roll no.": "studentRoll",
  "registration no.": "registrationNo",
  gender: "studentGender",
  "father's name": "fatherName",
  "mother's name": "motherName",
  religion: "studentReligion",
  "mobile no.": "guardianMobile",
  "blood group": "bloodGroup",
};

/**
 * Direct port of digicampusbd's `student-excel-registration-with-custom-id`
 * ("Excel Custom ID"). Confirmed via `StudentRegistrationService
 * .doExcelRegistrationWithStudentId` that the old app parses the uploaded
 * .xlsx entirely client-side (never uploads the file itself to the server)
 * and posts plain per-row JSON — the exact same shape the already-existing
 * single-student `POST /api/students` (createStudent) already accepts. This
 * port keeps that same client-side-parse-then-call-the-existing-endpoint
 * shape (once per row) instead of adding a new bulk backend endpoint —
 * **zero new backend** needed, since `createStudent` already auto-generates
 * `customStudentId` when blank (used by the "without custom id" sibling
 * page) and already validates the same required fields.
 */
export default function StudentExcelRegistrationWithIdPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    groupApi.list().then((list) => {
      setGroups(list);
      if (list.length > 0) setGroupId(String(list[0].id));
    });
    categoryApi.list().then((list) => {
      setCategories(list);
      if (list.length > 0) setCategoryId(String(list[0].id));
    });
  }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError("");
    setResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const mapped = raw.map((r) => {
          const row = {};
          Object.keys(r).forEach((header) => {
            const key = COLUMN_MAP[header.trim().toLowerCase()];
            if (key) row[key] = String(r[header]).trim();
          });
          return row;
        });
        setRows(mapped);
      } catch (err) {
        setError("Failed to parse the Excel file. Please use the provided template.");
      }
    };
    reader.readAsBinaryString(file);
  };

  const updateCell = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = async () => {
    setError("");
    if (!academicYear || !classConfigId || !groupId || !categoryId) {
      setError("Please select academic year, section, group, and category.");
      return;
    }
    if (rows.length === 0) {
      setError("Please upload a file with at least one row.");
      return;
    }
    setSubmitting(true);
    let success = 0;
    const failures = [];
    for (const row of rows) {
      try {
        await createStudent({
          studentName: row.studentName,
          studentGender: row.studentGender,
          studentReligion: row.studentReligion,
          fatherName: row.fatherName,
          motherName: row.motherName,
          guardianMobile: row.guardianMobile,
          bloodGroup: row.bloodGroup,
          registrationNo: row.registrationNo,
          customStudentId: row.customStudentId,
          academicYear,
          classConfigurationId: Number(classConfigId),
          groupId: Number(groupId),
          categoryId: Number(categoryId),
          studentRoll: row.studentRoll ? Number(row.studentRoll) : null,
        });
        success++;
      } catch (err) {
        failures.push(`${row.customStudentId || row.studentName}: ${(err.response && err.response.data && err.response.data.message) || "failed"}`);
      }
    }
    setResult({ success, failures });
    setSubmitting(false);
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Excel Registration — Custom ID</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {result && (
        <div>
          <p>
            Registered {result.success} of {rows.length} students.
          </p>
          {result.failures.length > 0 && (
            <ul>
              {result.failures.map((f, i) => (
                <li key={i} style={{ color: "var(--color-danger)" }}>
                  {f}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-row">
        <label>
          Upload Excel (.xlsx) — columns: Student ID, Name, Roll No., Registration No., Gender, Father's Name, Mother's
          Name, Religion, Mobile No., Blood Group
          <input type="file" accept=".xlsx,.xls" onChange={handleFile} />
        </label>
      </div>
      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Name</th>
                <th>Roll</th>
                <th>Registration No.</th>
                <th>Gender</th>
                <th>Father's Name</th>
                <th>Mother's Name</th>
                <th>Religion</th>
                <th>Mobile</th>
                <th>Blood Group</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input value={r.customStudentId || ""} onChange={(e) => updateCell(i, "customStudentId", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.studentName || ""} onChange={(e) => updateCell(i, "studentName", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.studentRoll || ""} onChange={(e) => updateCell(i, "studentRoll", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.registrationNo || ""} onChange={(e) => updateCell(i, "registrationNo", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.studentGender || ""} onChange={(e) => updateCell(i, "studentGender", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.fatherName || ""} onChange={(e) => updateCell(i, "fatherName", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.motherName || ""} onChange={(e) => updateCell(i, "motherName", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.studentReligion || ""} onChange={(e) => updateCell(i, "studentReligion", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.guardianMobile || ""} onChange={(e) => updateCell(i, "guardianMobile", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.bloodGroup || ""} onChange={(e) => updateCell(i, "bloodGroup", e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && (
        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting..." : `Register ${rows.length} Students`}
        </button>
      )}
    </MainLayout>
  );
}
